import React from 'react';
import PhoneInput from '../ui/PhoneInput';
import CustomDatePicker from '../ui/CustomDatePicker';

/**
 * DynamicFields component renders a set of inputs based on a configuration array.
 * 
 * @param {Array} fields - Array of field configuration objects from FieldConfiguration model
 * @param {Object} values - Current values for the fields
 * @param {Function} onChange - Callback when a field value changes
 * @param {Object} errors - Validation errors
 */
const DynamicFields = ({ fields, values, onChange, errors = {} }) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
      {fields.sort((a, b) => a.order - b.order).map((field) => {
        const fieldName = field.fieldName || field.name;
        const fieldLabel = field.fieldLabel || field.label;
        const fieldType = field.fieldType || field.type;
        let value = values[fieldName] ?? field.defaultValue ?? '';
        
        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            value = value.map(v => v?.name || v?.title || (typeof v === 'object' ? JSON.stringify(v) : v)).join(', ');
          } else {
            value = value._id || value.id || value.name || value.title || JSON.stringify(value);
          }
        }
        
        const error = errors[fieldName];

        return (
          <div key={field._id || field.label} className="flex flex-col space-y-2">
            {(fieldType !== 'phone' && fieldType !== 'date') && (
              <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                {fieldLabel}
                {(field.required || field.validation?.required) && <span className="text-red-500">*</span>}
              </label>
            )}

            {fieldType === 'textarea' ? (
              <textarea
                value={value ?? ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
                className={`px-4 py-2.5 bg-slate-50 border ${error ? 'border-red-500' : 'border-slate-200'} rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all min-h-[100px] resize-y font-medium text-slate-700`}
              />
            ) : fieldType === 'select' || fieldType === 'dropdown' ? (
              <select
                value={value ?? ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                className={`px-4 py-2.5 bg-slate-50 border ${error ? 'border-red-500' : 'border-slate-200'} rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium text-slate-700`}
              >
                <option value="">Select {fieldLabel}</option>
                {field.options?.map((opt) => (
                  <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
                    {typeof opt === 'string' ? opt : opt.label}
                  </option>
                ))}
              </select>
            ) : fieldType === 'checkbox' ? (
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => onChange(fieldName, e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-600">Enable {fieldLabel}</span>
              </div>
            ) : fieldType === 'phone' ? (
              <PhoneInput
                label={fieldLabel}
                value={value}
                onChange={(val) => onChange(fieldName, val)}
                error={error}
                required={field.required || field.validation?.required}
              />
            ) : fieldType === 'date' ? (
              <CustomDatePicker
                label={fieldLabel}
                name={fieldName}
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={field.required || field.validation?.required}
                validationError={error}
              />
            ) : (
              <input
                type={fieldType === 'number' ? 'number' : fieldType === 'email' ? 'email' : 'text'}
                value={value ?? ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                placeholder={field.placeholder || `Enter ${fieldLabel.toLowerCase()}...`}
                className={`px-4 py-2.5 bg-slate-50 border ${error ? 'border-red-500' : 'border-slate-200'} rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium text-slate-700`}
              />
            )}

            {error && (
              <span className="text-xs font-bold text-red-500 pl-1">
                {error}
              </span>
            )}
            
            {field.description && !error && (
              <span className="text-xs font-medium text-slate-400 pl-1">
                {field.description}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicFields;
